import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from "@angular/forms";

import { Subscription } from "rxjs";

import { getNbCharsByLinePerfumeEngraving } from "@hermes/fragments/product-utils";
import { AddToCartFacade } from "@hermes/states/add-to-cart";
import {
    FetchProductService,
    ProductPageFacade,
} from "@hermes/states/product-page";

export const MASK_PATTERN_CHARACTERS =
    /[A-Za-zÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÑÒÓÔÕÖØÙÚÛÜÝßàáâãäåæçèéêëìíîïñòóôõöøùúûüýÿĀāĂăĄąĆćĈĉĊċČčĎďĐđĒēĔĕĖėĘęĚěĜĝĞğĠġĢģĤĥĦħĨĩĪīĬĭĮįİĲĳĴĵĶķĹĺĻļĿŀŁłŃńŅņŇňŊŋŌōŎŏŐőŒœŔŕŖŗŘřŚśŜŝŞşŠšŢţŤťŦŧŨũŪūŬŭŮůŰűŲųŴŵŶŷŸŹźŻżŽž]/;
export const MASK_PATTERN_NUMBERS = /\d/;
export const MASK_PATTERN_SPECIAL_CHARACTERS = /[ '.-]/;

@Component({
    selector: "h-perfume-engraving-selector",
    templateUrl: "./perfume-engraving-selector.component.html",
    styleUrls: ["./perfume-engraving-selector.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerfumeEngravingSelectorComponent implements OnInit, OnDestroy {
    @Input() public formGroup: FormGroup;
    @Input() public storeEngravingLineByVolumeVariant: Map<
        string,
        { [key: string]: string }
    >;

    public nbCharsByLinePerfumeEngraving: number[];
    public instructionTranslateKey: string;
    public readonly maxLengthForInitial = 2;
    public charactersInErrorByLine: string[] = [];
    private formInfosPerfumeEngraving: FormGroup = new FormGroup({});
    private subscription: Subscription = new Subscription();
    private engravingType: string;
    private readonly INFOS_PERFUME_ENGRAVING = "infosPerfumeEngraving";
    private readonly PERFUME_ENGRAVING_LINE = "perfumeEngravingLine";
    private volumeSku: string;

    constructor(
        private fetchProductService: FetchProductService,
        private productPageFacade: ProductPageFacade,
        private addToCartFacade: AddToCartFacade,
    ) {}

    public get formAttributeList(): string[] {
        return Object.keys(this.infosPerfumeEngravingControls);
    }

    public get infosPerfumeEngravingControls(): {
        [key: string]: AbstractControl<string>;
    } {
        return this.formInfosPerfumeEngraving.controls;
    }

    public get infosPerfumeEngraving(): { [key: string]: string } {
        return this.formInfosPerfumeEngraving.value;
    }

    public get inputIndexesError(): number[] {
        const indexesInputInError = [];
        this.formAttributeList.forEach((attribute, index) => {
            const isInputRegexError = Boolean(
                this.infosPerfumeEngravingControls[attribute]?.errors?.pattern,
            );
            if (isInputRegexError) {
                indexesInputInError.push(index);
            }
        });
        return indexesInputInError;
    }

    public ngOnInit(): void {
        this.subscription.add(
            this.productPageFacade.currentProduct$.subscribe((product) => {
                this.volumeSku = product.sku;
                this.initOrUpdateInfoPerfumeEngraving(
                    product.simpleAttributes.engravingOptions,
                );
                this.engravingType = product.simpleAttributes.engravingOptions;
                this.instructionTranslateKey = `hermes.product-page.engraving-instruction-${this.engravingType}`;
            }),
        );
        this.subscription.add(
            this.fetchProductService.isVariantVolumeChanged$.subscribe(
                (isVariantVolumeChanged) => {
                    if (isVariantVolumeChanged) {
                        this.storeEngravingLineByVolumeVariant.set(
                            this.volumeSku,
                            this.infosPerfumeEngraving,
                        );
                        this.fetchProductService.isVariantVolumeChanged$.next(
                            false,
                        );
                    }
                },
            ),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public placeholder(index: number): string {
        return `hermes.product-page.engraving-placeholder-${this.engravingType}-line-${index}`;
    }

    private initOrUpdateInfoPerfumeEngraving(engravingOption: string): void {
        this.nbCharsByLinePerfumeEngraving =
            getNbCharsByLinePerfumeEngraving(engravingOption);
        const previousNbFormControls = this.formAttributeList.length;
        // Add or update formControls about engravingOption (call if variant volume is modified)
        for (
            let index = 0;
            index < this.nbCharsByLinePerfumeEngraving.length;
            index++
        ) {
            const currentFormControlName = `${this.PERFUME_ENGRAVING_LINE}${
                index + 1
            }`;
            const currentFormControlExist =
                this.formInfosPerfumeEngraving.contains(currentFormControlName);
            const initData =
                this.storeEngravingLineByVolumeVariant.get(this.volumeSku)?.[
                    currentFormControlName
                ] ?? "";
            if (currentFormControlExist) {
                this.infosPerfumeEngravingControls[
                    currentFormControlName
                ].setValue(initData);
                this.infosPerfumeEngravingControls[
                    currentFormControlName
                ].setValidators(this.initValidators(index));
            } else {
                const formControlPerfumeEngravingLine = new FormControl(
                    initData,
                    this.initValidators(index),
                );
                this.formInfosPerfumeEngraving.addControl(
                    currentFormControlName,
                    formControlPerfumeEngravingLine,
                );
            }
        }

        if (this.nbCharsByLinePerfumeEngraving.length === 0) {
            this.formGroup.removeControl(this.INFOS_PERFUME_ENGRAVING);
            return;
        }
        // Removes excess formcontrols according to the engravingOption when the volume variant is modified
        for (
            let index = this.nbCharsByLinePerfumeEngraving.length + 1;
            index <= previousNbFormControls;
            index++
        ) {
            const currentFormControlName = `${this.PERFUME_ENGRAVING_LINE}${index}`;
            this.formInfosPerfumeEngraving.removeControl(
                currentFormControlName,
            );
        }

        if (!this.formGroup.contains(this.INFOS_PERFUME_ENGRAVING)) {
            this.formGroup.addControl(
                this.INFOS_PERFUME_ENGRAVING,
                this.formInfosPerfumeEngraving,
            );
            // Enabled / disabled ATC button when change volume variant with value prefill
            this.addToCartFacade.changeButtonEnabledState(
                0,
                this.formInfosPerfumeEngraving.valid,
            );
            // Enabled / disabled ATC button when change current value
            this.subscription.add(
                this.formInfosPerfumeEngraving.statusChanges.subscribe(
                    (status) => {
                        const isValid = status === "VALID";
                        const isInvalid = status === "INVALID";
                        if (isValid || isInvalid) {
                            this.addToCartFacade.changeButtonEnabledState(
                                0,
                                isValid,
                            );
                        }
                    },
                ),
            );
        }
    }

    private initValidators(index: number): ValidatorFn[] {
        return [
            Validators.maxLength(this.nbCharsByLinePerfumeEngraving[index]),
            this.validatePattern(index),
        ];
    }

    private validatePattern(index: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            if (control.value) {
                const availableCharacters =
                    this.nbCharsByLinePerfumeEngraving[index];
                const isValid = this.getPattern(availableCharacters, true).test(
                    control.value,
                );
                // Use custom validator to add message error in view (override Validators.pattern)
                this.charactersInErrorByLine[index] = isValid
                    ? undefined
                    : ` ${control.value.replace(
                          this.getPattern(availableCharacters, false),
                          "",
                      )}`;
                return isValid ? {} : { pattern: true };
            }
            return {};
        };
    }

    private getPattern(
        availableCharactersCount: number,
        isRegexWithAnchors,
    ): RegExp {
        const isForInitial =
            availableCharactersCount <= this.maxLengthForInitial;
        const additionnalPattern = isForInitial
            ? ""
            : `|${MASK_PATTERN_NUMBERS.source}|${MASK_PATTERN_SPECIAL_CHARACTERS.source}`;
        const pattern = `${MASK_PATTERN_CHARACTERS.source}${additionnalPattern}`;
        return isRegexWithAnchors
            ? new RegExp(`^(${pattern}){0,${availableCharactersCount}}$`)
            : new RegExp(`(${pattern}){0,${availableCharactersCount}}`, "g");
    }
}
